import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type SermonQuestionStatus = 'submitted' | 'queued' | 'answered';

export interface SermonQuestion {
  id: string;
  sermon_id: string;
  submitter_id?: string | null;
  submitter_name?: string | null;
  submitter_source: 'mobile' | 'website' | 'admin';
  question_text: string;
  status: SermonQuestionStatus;
  admin_response?: string | null;
  created_at: string;
  queued_at?: string | null;
  answered_at?: string | null;
  sermon_title?: string;
}

interface QuestionQueryOptions {
  sermonId?: string;
  statuses?: SermonQuestionStatus[];
}

const buildQuery = async ({ sermonId, statuses }: QuestionQueryOptions): Promise<SermonQuestion[]> => {
  let query = supabase
    .from('sermon_questions')
    .select('id, sermon_id, submitter_id, submitter_name, submitter_source, question_text, status, admin_response, created_at, queued_at, answered_at, sermons(title)')
    .order('created_at', { ascending: false });

  if (sermonId) {
    query = query.eq('sermon_id', sermonId);
  }

  if (statuses && statuses.length === 1) {
    query = query.eq('status', statuses[0]);
  } else if (statuses && statuses.length > 1) {
    query = query.in('status', statuses);
  }

  const { data, error } = await query;
  if (error) throw error;
  return ((data ?? []) as any[]).map((row) => ({
    id: row.id,
    sermon_id: row.sermon_id,
    submitter_id: row.submitter_id,
    submitter_name: row.submitter_name,
    submitter_source: row.submitter_source,
    question_text: row.question_text,
    status: row.status,
    admin_response: row.admin_response,
    created_at: row.created_at,
    queued_at: row.queued_at,
    answered_at: row.answered_at,
    sermon_title: row.sermons?.title,
  })) as SermonQuestion[];
};

export const useSermonQuestions = (options: QuestionQueryOptions) => {
  const queryClient = useQueryClient();
  const sermonIdFilter = options.sermonId ?? 'all';
  const statusesFilter = useMemo(() => options.statuses ?? ['all'], [options.statuses]);
  const statusesFilterKey = statusesFilter.join('|');
  const queryKey = useMemo(
    () => ['sermon-questions', sermonIdFilter, ...statusesFilter],
    [sermonIdFilter, statusesFilter]
  );

  useEffect(() => {
    const channel = supabase
      .channel(`sermon-questions-live-${sermonIdFilter}-${statusesFilter.join('-')}-${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sermon_questions' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [options.sermonId, options.statuses, queryClient, queryKey, sermonIdFilter, statusesFilter, statusesFilterKey]);

  return useQuery<SermonQuestion[]>({
    queryKey,
    queryFn: () => buildQuery(options),
    enabled: Boolean(options.sermonId || options.statuses),
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });
};

interface UpdateQuestionStatusPayload {
  questionId: string;
  nextStatus: SermonQuestionStatus;
  actorId?: string;
  adminResponse?: string | null;
}

export const useUpdateSermonQuestionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ questionId, nextStatus, actorId, adminResponse }: UpdateQuestionStatusPayload) => {
      const updatePayload: Record<string, unknown> = {
        status: nextStatus,
      };

      if (nextStatus === 'queued') {
        updatePayload.queued_at = new Date().toISOString();
        updatePayload.queued_by = actorId ?? null;
      }

      if (nextStatus === 'answered') {
        updatePayload.answered_at = new Date().toISOString();
        updatePayload.answered_by = actorId ?? null;
        if (adminResponse !== undefined) {
          const trimmed = adminResponse?.trim() ?? '';
          updatePayload.admin_response = trimmed.length > 0 ? trimmed : null;
        }
      }

      const { error } = await supabase
        .from('sermon_questions')
        .update(updatePayload)
        .eq('id', questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
    },
  });
};

interface UpdateQuestionResponsePayload {
  questionId: string;
  adminResponse: string;
}

export const useUpdateSermonQuestionResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ questionId, adminResponse }: UpdateQuestionResponsePayload) => {
      const trimmed = adminResponse.trim();
      const { error } = await supabase
        .from('sermon_questions')
        .update({
          admin_response: trimmed.length > 0 ? trimmed : null,
        })
        .eq('id', questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
    },
  });
};

interface DeleteQuestionPayload {
  questionId: string;
}

export const useDeleteSermonQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ questionId }: DeleteQuestionPayload) => {
      const { error } = await supabase
        .from('sermon_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
    },
  });
};
